import { map } from 'lodash'

import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { WPFile, WPLink, WPPageData } from '@/types'

export const PostType = {
  POST: 'post',
  PRESS: 'press',
  TUTOR: 'tutor',
  COURSE: 'course',
  PROJECT: 'project',
  SERVICE: 'service',
  CERTIFICATION: 'certification'
}

export const normalizeWPDate = (date: string, locale?: string) => {
  const [year, month, day] = date.split('T')[0].split('-')
  return locale === 'it' ? `${day}/${month}/${year}` : `${year}/${month}/${day}`
}

export const normalizeWPLink = (
  type: string,
  link: WPLink,
  upload: WPFile,
  labels: Record<string, string>
) => {
  const linkLabelsMap = {
    [PostType.POST]: labels?.read_more,
    [PostType.PRESS]: labels?.discover_more,
    [PostType.COURSE]: labels?.discover_more,
    [PostType.PROJECT]: labels?.discover_more,
    [PostType.CERTIFICATION]: labels?.download
    // [PostType.SERVICE]: labels?.discover_more,
    // [PostType.TUTOR]: labels?.discover_more,
  }

  const title = linkLabelsMap[type]

  return (
    (type && link
      ? title
        ? {
            title,
            url: upload?.url || link,
            target: upload?.url ? '_blank' : ''
          }
        : null
      : link) || null
  )
}

export const normalizeWPTaxonomies = (type: string, taxonomies: any[]) => {
  const taxonomiesMap = {
    [PostType.TUTOR]: 'subject',
    [PostType.PROJECT]: 'project_category'
    // [PostType.POST]: 'category',
    // [PostType.COURSE]: 'course_tag',
  }

  const taxonomy = taxonomiesMap[type] as any

  const prevent =
    type === PostType.PROJECT && taxonomies[taxonomy][0].slug === 'progetto'

  return type && taxonomy
    ? map(taxonomies && !prevent ? taxonomies[taxonomy] : [], 'name').join(', ')
    : null
}

export const getBadgeFromWPTaxonomies = (type: string, taxonomies: any[]) => {
  const taxonomiesMap = {
    [PostType.PROJECT]: 'project_status'
  }

  const taxonomy = taxonomiesMap[type] as any

  return type && taxonomy
    ? map(taxonomies ? taxonomies[taxonomy] : [], 'name').join(', ')
    : null
}

export const normalizeWPContent = (
  type: string,
  content: any,
  date: string,
  locale?: string
) => {
  return type === 'press'
    ? `${normalizeWPDate(date, locale)}${content?.rendered ? ` - ${content?.rendered}` : ''}`
    : content?.rendered
}

export const normalizePostsCollection = (
  collection: any[],
  { labels }: WPPageData,
  locale?: string
) => {
  return map(
    collection,
    (
      {
        id,
        image,
        title,
        overline,
        paragraph,
        content,
        type,
        date,
        link,
        taxonomies,
        featured_image,
        acf
      }: any,
      index
    ) => {
      return {
        uuid: `${id || index + 1}`,
        image: featured_image || image || null,
        title: title?.rendered || title || null,
        badge: getBadgeFromWPTaxonomies(type, taxonomies),
        overline: overline || normalizeWPTaxonomies(type, taxonomies),
        paragraph:
          normalizeWPContent(type, content, date, locale) || paragraph || null,
        link: normalizeWPLink(type, link, acf?.upload, labels)
      }
    }
  )
}

export const normalizeRelationalSection = (
  section: any,
  pageData: WPPageData,
  locale?: string
) => {
  const type = section.collection_type
  const collection = section[type] || section.collection

  return {
    ...section,
    collection: normalizePostsCollection(collection, pageData, locale)
  }
}

export const normalizeRelationalArchive = async (
  section: any,
  pageData: WPPageData,
  locale?: string
) => {
  const { labels } = pageData

  return {
    ...(await normalizeRelationalSection(section, pageData, locale)),
    ...(section.filter_terms.length > 0
      ? {
          filters: {
            filter: {
              label: labels?.filter_by,
              placeholder: labels?.selection,
              options: map(section.filter_terms, ({ name, term_id }: any) => ({
                label: name,
                value: term_id
              }))
            }
          }
        }
      : null)
  }
}

export const resolveGravityFieldsFromFormId = async (section: any) => {
  const {
    data: { fields }
  } = await axios.get(`${API.GRAVITY_FORM}/${section.form_id}/`)

  return {
    ...section,
    form: fields
  }
}

export const mapPageStaticProps = async (
  { acf, ...pageData }: WPPageData,
  locale?: string
) => {
  return {
    ...pageData,
    acf: {
      ...acf,
      builder: await Promise.all(
        map(acf.builder, async (section: any) =>
          section.acf_fc_layout === 'image_card_grid' ||
          section.acf_fc_layout === 'project_grid' ||
          section.acf_fc_layout === 'archive_grid' ||
          section.acf_fc_layout === 'pillars_grid' ||
          section.acf_fc_layout === 'slider'
            ? normalizeRelationalSection(section, pageData, locale)
            : section.acf_fc_layout === 'archive_post_types'
              ? normalizeRelationalArchive(section, pageData, locale)
              : section.acf_fc_layout === 'contact_form'
                ? resolveGravityFieldsFromFormId(section)
                : section
        )
      )
    }
  }
}

export const mapHomepageStaticProps = async ({
  acf,
  ...pageData
}: WPPageData) => ({
  ...pageData,
  acf: {
    ...acf,
    newsletter: acf.newsletter
      ? await resolveGravityFieldsFromFormId(acf.newsletter)
      : null
  }
})
